import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { GuestAccessInfo, GuestSessionSummary } from '@claude-code-api/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { SharedSessionAccessGate } from '@/components/shared/SharedSessionAccessGate';
import { getSessionOpenUrl } from '@/lib/gift-agent';

export default function SharedGiftAgentPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<GuestSessionSummary | null>(null);
  const [accessInfo, setAccessInfo] = useState<GuestAccessInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  const fetchSharedSession = useCallback(async () => {
    if (!sessionId) return false;

    const sessionResponse = await fetch(`/api/sessions/${sessionId}/share`, { credentials: 'include' });
    if (!sessionResponse.ok) {
      setSession(null);
      setAccessInfo(null);
      return false;
    }

    const sessionData = await sessionResponse.json();
    const nextSession = sessionData.data as GuestSessionSummary;
    setSession(nextSession);

    if (nextSession.runnerType !== 'acpx' && nextSession.runnerType !== 'acpx-experimental') {
      navigate(`/share/session/${sessionId}`, { replace: true });
      return false;
    }

    const accessResponse = await fetch(`/api/sessions/${sessionId}/share/access`, { credentials: 'include' });
    if (accessResponse.ok) {
      const accessData = await accessResponse.json();
      setAccessInfo(accessData.data);
    } else {
      setAccessInfo(null);
    }

    return true;
  }, [navigate, sessionId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const ok = await fetchSharedSession();
        if (cancelled) return;
        if (!ok) {
          setSession(null);
        }
      } catch (error) {
        console.error('Failed to load shared agent:', error);
        if (!cancelled) {
          setSession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchSharedSession]);

  useEffect(() => {
    if (!session || !accessInfo || (session.runnerType !== 'acpx' && session.runnerType !== 'acpx-experimental') || session.status !== 'running') {
      return;
    }
    window.location.replace(getSessionOpenUrl(session.id, { shared: true }));
  }, [session, accessInfo]);

  const verifyAccess = async (password: string) => {
    if (!sessionId) return;
    setAuthorizing(true);
    setAccessError(null);
    try {
      const response = await fetch(`/api/sessions/${sessionId}/share/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAccessError(String(data?.error || 'Access denied'));
        return;
      }

      const ok = await fetchSharedSession();
      if (!ok) {
        setAccessError('Session is no longer available');
      }
    } catch {
      setAccessError('Failed to verify password');
    } finally {
      setAuthorizing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background">
        <div className="space-y-4 w-64">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!session || !accessInfo) {
    return (
      <SharedSessionAccessGate
        title="Shared GIFT Agent access"
        description="Enter the shared session password to use this agent and container."
        loading={authorizing}
        error={accessError}
        onSubmit={verifyAccess}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background">
      <p className="text-muted-foreground">Opening ACPX workspace...</p>
    </div>
  );
}
